import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";

export default function CoursesPage() {
    const [courses, setCourses] = useState([]);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("All");

    useEffect(() => {
        api.get("/courses").then(r => setCourses(r.data));
    }, []);

    const categories = ["All", ...Array.from(new Set(courses.map(c => c.category)))];
    const filtered = courses.filter(c =>
        (cat === "All" || c.category === cat) &&
        (c.name.toLowerCase().includes(q.toLowerCase()) || c.full_name.toLowerCase().includes(q.toLowerCase()))
    );

    return (
        <div className="min-h-screen paper-grain" data-testid="courses-page">
            <Navbar />
            <section className="mx-auto max-w-7xl px-6 pt-16 pb-10">
                <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">The Catalog</div>
                <h1 className="font-serif text-5xl md:text-6xl text-ink leading-none">Course Library</h1>
                <p className="text-ink/70 mt-4 max-w-2xl">Choose your exam. Every course includes AI-generated practice tests, full-length mocks, and detailed mistake analysis.</p>

                <div className="mt-10 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
                    <div className="relative max-w-md w-full">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40"/>
                        <Input data-testid="input-course-search" placeholder="Search exams…" value={q} onChange={e => setQ(e.target.value)} className="pl-9 rounded-sm border-ink/20"/>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {categories.map(c => (
                            <button key={c} onClick={() => setCat(c)} data-testid={`filter-${c.toLowerCase().replace(/\s+/g,'-')}`}
                                className={`text-xs px-3 py-1.5 rounded-sm border transition-colors duration-150 ${
                                    cat === c ? "bg-ink text-bone border-ink" : "bg-transparent text-ink border-ink/20 hover:border-ink/50"
                                }`}>
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-6 pb-24">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map(c => (
                        <Link key={c.id} to={`/courses/${c.id}`} data-testid={`course-card-${c.id}`}
                              className="group block border border-ink/15 bg-bone rounded-sm overflow-hidden hover:border-ink/50 hover:-translate-y-1 transition-[transform,border-color] duration-200">
                            <div className="h-2" style={{ backgroundColor: c.color }}></div>
                            <div className="p-6">
                                <div className="text-xs uppercase tracking-widest text-ink/50 mb-2">{c.category}</div>
                                <div className="font-serif text-2xl text-ink">{c.name}</div>
                                <div className="text-sm text-ink/60 mt-1">{c.full_name}</div>
                                <div className="mt-6 flex flex-wrap gap-1.5">
                                    {c.topics.slice(0, 4).map(t => (
                                        <span key={t} className="text-[10px] px-2 py-0.5 border border-ink/15 text-ink/70 rounded-sm">{t}</span>
                                    ))}
                                    {c.topics.length > 4 && <span className="text-[10px] text-ink/50 px-2 py-0.5">+{c.topics.length - 4} more</span>}
                                </div>
                                <div className="mt-6 flex items-center justify-between text-xs text-ink/60">
                                    <span>
                                        {c.subjects ? `${c.subjects.length} subjects · ${c.subjects.reduce((n, s) => n + s.chapters.length, 0)} chapters` : `${c.questions} Qs`}
                                    </span>
                                    <ArrowRight size={16} className="text-ink group-hover:translate-x-1 transition-transform duration-150"/>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
                {filtered.length === 0 && (
                    <div className="text-center py-20 text-ink/50" data-testid="no-courses">No courses match your search.</div>
                )}
            </section>
            <Footer />
        </div>
    );
}
